import { useEffect, useState } from 'react';
import type { Ticket } from '../../../types/ticket';
import { getExpectedResponseTime } from '../../../utils/ticketIntelligence';
import './SubmissionConfirmation.css';

interface SubmissionConfirmationProps {
  ticket: Ticket;
  onSubmitAnother: () => void;
  onBrowseKB: () => void;
}

export function SubmissionConfirmation({
  ticket,
  onSubmitAnother,
  onBrowseKB,
}: SubmissionConfirmationProps) {
  const [showContent, setShowContent] = useState(false);

  // Stagger animation
  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const expectedResponse = getExpectedResponseTime(ticket.priority);

  const formatTime = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(new Date(date));
  };

  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    }).format(new Date(date));
  };

  return (
    <div className="submission-confirmation">
      {/* Success animation */}
      <div className="submission-confirmation__hero">
        <div className="submission-confirmation__check-container">
          <svg
            className="submission-confirmation__check"
            width="80"
            height="80"
            viewBox="0 0 80 80"
            fill="none"
          >
            <circle
              className="submission-confirmation__check-circle"
              cx="40"
              cy="40"
              r="36"
              stroke="#107c10"
              strokeWidth="4"
            />
            <path
              className="submission-confirmation__check-mark"
              d="M24 40l12 12 20-24"
              stroke="#107c10"
              strokeWidth="5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h1 className="submission-confirmation__title">You're all set!</h1>
        <p className="submission-confirmation__subtitle">
          Your request has been submitted and our team is on it.
        </p>
      </div>

      {/* Ticket info card */}
      <div className={`submission-confirmation__card ${showContent ? 'submission-confirmation__card--visible' : ''}`}>
        <div className="submission-confirmation__ticket-header">
          <span className="submission-confirmation__ticket-label">Request Number</span>
          <span className="submission-confirmation__ticket-number">{ticket.ticketNumber}</span>
        </div>

        <div className="submission-confirmation__summary">
          <p className="submission-confirmation__description">{ticket.shortDescription}</p>
          <div className="submission-confirmation__meta">
            <span>Submitted at {formatTime(ticket.createdAt)}</span>
            <span className="submission-confirmation__meta-dot">•</span>
            <span>{formatDate(ticket.createdAt)}</span>
          </div>
        </div>
      </div>

      {/* What happens next */}
      <div className={`submission-confirmation__next-steps ${showContent ? 'submission-confirmation__next-steps--visible' : ''}`}>
        <h2 className="submission-confirmation__section-title">What happens next</h2>

        <div className="submission-confirmation__timeline">
          <div className="submission-confirmation__timeline-item submission-confirmation__timeline-item--complete">
            <div className="submission-confirmation__timeline-icon">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 8l4 4 6-8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="submission-confirmation__timeline-content">
              <span className="submission-confirmation__timeline-title">Request received</span>
              <span className="submission-confirmation__timeline-desc">Just now</span>
            </div>
          </div>

          <div className="submission-confirmation__timeline-item submission-confirmation__timeline-item--active">
            <div className="submission-confirmation__timeline-icon">
              <div className="submission-confirmation__timeline-pulse" />
            </div>
            <div className="submission-confirmation__timeline-content">
              <span className="submission-confirmation__timeline-title">Being reviewed</span>
              <span className="submission-confirmation__timeline-desc">
                A technician will review and assign your request
              </span>
            </div>
          </div>

          <div className="submission-confirmation__timeline-item">
            <div className="submission-confirmation__timeline-icon">
              <span>3</span>
            </div>
            <div className="submission-confirmation__timeline-content">
              <span className="submission-confirmation__timeline-title">You'll hear from us</span>
              <span className="submission-confirmation__timeline-desc">
                Expected within <strong>{expectedResponse}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Contact info */}
      <div className={`submission-confirmation__contact ${showContent ? 'submission-confirmation__contact--visible' : ''}`}>
        <div className="submission-confirmation__contact-item">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="2" y="4" width="16" height="12" rx="2" />
            <path d="M2 6l8 5 8-5" />
          </svg>
          <div className="submission-confirmation__contact-text">
            <span className="submission-confirmation__contact-label">Confirmation sent to</span>
            <span className="submission-confirmation__contact-value">{ticket.requesterEmail}</span>
          </div>
        </div>

        <div className="submission-confirmation__contact-item">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="10" cy="10" r="8" />
            <path d="M10 6v4l2.5 1.5" strokeLinecap="round" />
          </svg>
          <div className="submission-confirmation__contact-text">
            <span className="submission-confirmation__contact-label">Need it faster?</span>
            <span className="submission-confirmation__contact-value">Call IT Support: ext. 5555</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className={`submission-confirmation__actions ${showContent ? 'submission-confirmation__actions--visible' : ''}`}>
        <button
          type="button"
          className="submission-confirmation__btn submission-confirmation__btn--primary"
          onClick={onSubmitAnother}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 4v10M4 9h10" strokeLinecap="round" />
          </svg>
          Submit another request
        </button>
        <button
          type="button"
          className="submission-confirmation__btn"
          onClick={onBrowseKB}
        >
          Browse Knowledge Base
        </button>
      </div>

      {/* Footer tip */}
      <p className="submission-confirmation__tip">
        <strong>Pro tip:</strong> Reply to the confirmation email to add more details or attachments to your request.
      </p>
    </div>
  );
}
